import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { CartService } from '@services/cart-service/cart.service';
import { AppConfig } from 'client/app/app.config';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-product-controller',
  templateUrl: './product-controller.component.html',
  standalone: false,
})
export class ProductControllerComponent implements OnInit, OnDestroy {
  user: any;
  experiments: any[];
  product: any;
  article: any;
  cart: any;
  numberOfProductsInCart: number;
  productInCart: number;
  loading: boolean = true;
  isAddRemovePending: boolean = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public appConfig: AppConfig,
    private conn: ConnectionService,
    private broadcast: BroadcastService,
    private eventLogger: EventLoggerService,
    private cartService: CartService,
  ) { }

  async ngOnInit(): Promise<void> {
    // Load initial product data
    await this.loadProductData(this.route.snapshot.params.id);

    // Listen for route param changes (when navigating between products)
    this.subscriptions.push(
      this.route.params.subscribe(async (params): Promise<void> => {
        const productId = params['id'];
        if (productId && this.product?.objectId !== productId) {
          this.loading = true;
          await this.loadProductData(productId);
        }
      })
    );

    // Listen for cart updates from cart service to refresh UI
    // Note: getCart() is called here to update UI when cart changes from other components
    // When adding via handleBuyNow, we navigate away so no need to update here
    this.subscriptions.push(
      this.broadcast.on<any>('CART_UPDATED')
        .subscribe(async (): Promise<void> => {
          // Only update cart if we're still on this page (not navigating away)
          if (!this.isAddRemovePending) {
            await this.getCart();
          }
        })
    );
  }

  private async loadProductData(productId: string): Promise<void> {
    this.user = this.conn.getActingUser();
    if (!this.user) return;
    this.experiments = await this.conn.findUserActiveExperiments(this.user.get('username'));
    const [product]: any[] = await this.conn.findCatalogWithKey([productId]);
    const articleExperiment = this.experiments.find((item:any):any => item.name === 'complex_article');
    if (product?.get('articleWithTag') && articleExperiment?.variant.productIds?.includes(productId)) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({
        name: 'product-complex-article-page-visit',
        value: productId,
      }));
      const articleId = (<{ id: string }>Object.values(product.get('articleWithTag'))[0]).id;
      this.article = await this.conn.getArticleById(articleId, { translate: true });
    // } else if (product?.get('articleWithTag') &&
    // this.user?.get('marketingTags').includes(Object.keys(product.get('articleWithTag'))[0])) {
    // const articleId = (<{ id: string }>Object.values(product?.get('articleWithTag'))[0]).id;
    // this.article = await this.conn.getArticleById(articleId, { translate: true });
    // }
    } else if (product?.get('articleId') || (product?.get('article') && product.get('article')?.id)) {
      const articleId = product?.get('articleId') || product.get('article')?.id;
      this.article = await this.conn.getArticleById(articleId, { translate: true });
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'product-article-page-visit',
        value: productId }));
    }
    this.product = JSON.parse(JSON.stringify(product));

    // Remove article if it's not available for user langage.
    const isTranslationAvailalbeForArticle = this.article?.get('params')?.articleImagesLanguageString?.map(
      (languageString: any): string => languageString.get(this.user.get('languagePreference') || this.appConfig.Shared.Languages.EN))
      .every((str: string): boolean => !!str);
    if (!isTranslationAvailalbeForArticle) this.article = null;

    await this.getCart();
    this.loading = false;
  }

  async getCart(): Promise<void> {
    this.numberOfProductsInCart = 0;
    this.cart = await this.conn.getCart();
    this.cart.get('lineItems')?.forEach((element: any): void => this.numberOfProductsInCart += element.quantity);
    this.productInCart = this.cart.get('lineItems')?.find((element: any): any => element.productId === this.product.objectId);
    this.isAddRemovePending = false;
  }

  async removeFromCart(productId: string): Promise<any> {
    this.isAddRemovePending = true;
    const quantity = this.cart.get('lineItems')?.find((product: any): any => product.productId === productId)?.quantity;
    if (!quantity || quantity <= 0) {
      this.isAddRemovePending = false;
      return;
    }

    const params = {
      productId,
      quantity,
    };
    await this.conn.removeProductFromCart(params);
    await this.getCart();
  }

  async handleBuyNow(productId: string): Promise<void> {
    // Check if product is already in cart
    const productObjectInCart = this.cart?.get('lineItems')?.find((each: any): any => each.productId === productId);

    if (!productObjectInCart?.quantity) {
      // Add product to cart using cart service (handles discontinued popup, etc.)
      const productsInCart: { [key: string]: number } = { [productId]: 0 };
      const result = await this.cartService.addOrRemoveFromCart(this.product, this.appConfig.Shared.Cart.Action.ADD, {
        productsInCart,
        cartItems: this.cart?.get('lineItems') || [],
        navigateToCartOnAdd: true,
        section: this.route.snapshot.queryParams?.section || 'productPage',
      } as any);

      // Only navigate if add was successful (user confirmed if popup appeared)
      if (result?.success) {
        await this.router.navigate(['cart']);
      } else {
        await this.getCart();
      }
    } else {
      await this.router.navigate(['cart']);
    }
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
    this.subscriptions = [];
  }
}
